package com.schoolconnect.app.feature.students

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.RowScope
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.feature.common.ShellTopRightActions
import com.schoolconnect.app.feature.common.shellAvatarInitials
import com.schoolconnect.app.feature.common.shellRoleLabel
import com.schoolconnect.app.feature.common.shellRoleSubtitle
import ischoolconnect.sharedui.generated.resources.ban
import ischoolconnect.sharedui.generated.resources.circle_check
import ischoolconnect.sharedui.generated.resources.trash

@Composable
fun StudentsScreen(
    state: StudentsUiState,
    onEvent: (StudentsUiEvent) -> Unit,
    onBack: () -> Unit = {},
    shellRoleCode: String = "",
    shellSchoolLabel: String = "iConnect.School",
    onChangeRole: () -> Unit = onBack,
    onLogout: () -> Unit = {},
    headerActions: @Composable RowScope.() -> Unit = {
        var expanded by remember { mutableStateOf(false) }
        ShellTopRightActions(
            roleLabel = shellRoleLabel(shellRoleCode),
            roleSubtitle = shellRoleSubtitle(shellRoleCode),
            initials = shellAvatarInitials(shellRoleLabel(shellRoleCode)),
            expanded = expanded,
            onExpandedChange = { expanded = it },
            onChangeRole = onChangeRole,
            onLogout = onLogout
        )
    }
) {
    var showFilterModal by remember { mutableStateOf(false) }
    var showStudentFormModal by remember { mutableStateOf(false) }
    var showViewModal by remember { mutableStateOf(false) }
    var editingStudentId by remember { mutableStateOf<String?>(null) }
    var formDraft by remember { mutableStateOf(StudentFormValues()) }
    var filterDraft by remember { mutableStateOf(StudentFilterValues()) }
    var filterSearchDraft by remember { mutableStateOf("") }
    var selectedStudent by remember { mutableStateOf<StudentRowUi?>(null) }
    var pendingStudentAction by remember { mutableStateOf<PendingStudentAction?>(null) }
    var rowsPerPageExpanded by remember { mutableStateOf(false) }

    val page = remember(state.students, state.paging, state.sortField, state.sortAscending) {
        studentsPage(state)
    }

    LaunchedEffect(page.items) {
        if (selectedStudent != null && page.items.none { it.id == selectedStudent?.id }) {
            selectedStudent = null
            showViewModal = false
        }
    }

    LaunchedEffect(state.editForm) {
        state.editForm?.let { formDraft = it }
    }

    LaunchedEffect(state.mutationSuccessNonce) {
        if (state.mutationSuccessNonce > 0L) {
            showStudentFormModal = false
            editingStudentId = null
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        StudentsScreenScaffold(
            state = state,
            onEvent = onEvent,
            headerActions = headerActions,
            onAddClicked = {
                editingStudentId = null
                formDraft = StudentFormValues()
                onEvent(StudentsUiEvent.StudentFormDismissed)
                showStudentFormModal = true
            },
            onFilterClicked = {
                filterDraft = state.filters
                filterSearchDraft = state.searchQuery
                showFilterModal = true
            },
            pageIndex = state.pageIndex,
            pageSize = state.pageSize,
            rowsPerPageExpanded = rowsPerPageExpanded,
            onRowsPerPageToggle = { rowsPerPageExpanded = !rowsPerPageExpanded },
            onRowsPerPageDismiss = { rowsPerPageExpanded = false },
            onPageSizeChanged = { onEvent(StudentsUiEvent.PageSizeChanged(it)) },
            onPageChanged = { onEvent(StudentsUiEvent.PageChanged(it)) },
            onEditStudent = { student ->
                editingStudentId = student.id
                formDraft = studentFormValuesFromRow(student)
                showStudentFormModal = true
                onEvent(StudentsUiEvent.EditStudent(student))
            },
            onViewStudent = { student ->
                selectedStudent = student
                showViewModal = true
            },
            onToggleStudent = { student ->
                pendingStudentAction = PendingStudentAction(student, PendingActionType.ToggleStatus)
            },
            onDeleteStudent = { student ->
                pendingStudentAction = PendingStudentAction(student, PendingActionType.Delete)
            },
            suppressMessageBanner = showStudentFormModal
        )

        StudentFilterModal(
            open = showFilterModal,
            searchQuery = filterSearchDraft,
            initialValues = filterDraft,
            classOptions = state.studentFilterClassOptions,
            sectionOptionsByClass = state.studentFilterSectionsByClass,
            onClose = { showFilterModal = false },
            onApply = { searchQuery, values ->
                filterSearchDraft = searchQuery
                filterDraft = values
                onEvent(StudentsUiEvent.FiltersApplied(searchQuery, values))
                showFilterModal = false
            },
            onReset = {
                filterSearchDraft = ""
                filterDraft = StudentFilterValues()
                onEvent(StudentsUiEvent.FiltersReset)
            }
        )

        StudentFormModal(
            open = showStudentFormModal,
            title = if (editingStudentId == null) "Add Student" else "Edit Student",
            subtitle = if (editingStudentId == null) {
                "Enter the student details to create a new student record."
            } else {
                "Update the student details and save the changes."
            },
            initialValues = formDraft.copy(id = editingStudentId.orEmpty()),
            subjectOptions = state.subjectOptions,
            classSectionOptions = state.classSectionOptions,
            classSectionPicker = state.classSectionPicker,
            onClassSectionSearchChanged = { onEvent(StudentsUiEvent.ClassSectionPickerSearchChanged(it)) },
            onClassSectionPageChanged = { onEvent(StudentsUiEvent.ClassSectionPickerPageChanged(it)) },
            onClassSectionLoadMore = { onEvent(StudentsUiEvent.ClassSectionPickerLoadMore) },
            onClassSectionOpened = { onEvent(StudentsUiEvent.ClassSectionPickerOpened) },
            showStatusField = editingStudentId != null,
            isSaving = state.isSaving,
            apiError = state.apiErrorMessage,
            onClose = {
                if (!state.isSaving) {
                    showStudentFormModal = false
                    onEvent(StudentsUiEvent.StudentFormDismissed)
                }
            },
            onSave = { values ->
                onEvent(StudentsUiEvent.SaveStudent(values.copy(id = editingStudentId.orEmpty())))
            },
            saveLabel = if (editingStudentId == null) "Add" else "Save"
        )

        selectedStudent?.let { student ->
            if (showViewModal) {
                StudentViewModal(
                    student = student,
                    onClose = {
                        showViewModal = false
                        selectedStudent = null
                    }
                )
            }
        }

        pendingStudentAction?.let { pending ->
            val student = pending.student
            val isActivate = student.statusCode != SchoolKeys.V_STATUS_ACTIVE
            val actionLabel = if (isActivate) "Activate" else "Inactivate"
            AppConfirmationDialog(
                onDismiss = { pendingStudentAction = null },
                title = when (pending.action) {
                    PendingActionType.Delete -> "Delete Student"
                    PendingActionType.ToggleStatus -> "$actionLabel Student"
                },
                subtitle = when (pending.action) {
                    PendingActionType.Delete -> "This will move the student to inactive records."
                    PendingActionType.ToggleStatus -> if (isActivate) {
                        "This will restore the student to active records."
                    } else {
                        "This will move the student to inactive records."
                    }
                },
                bodyText = when (pending.action) {
                    PendingActionType.Delete -> "Delete ${student.studentName}?"
                    PendingActionType.ToggleStatus -> "$actionLabel ${student.studentName}?"
                },
                confirmLabel = when (pending.action) {
                    PendingActionType.Delete -> "Delete"
                    PendingActionType.ToggleStatus -> actionLabel
                },
                onConfirm = {
                    when (pending.action) {
                        PendingActionType.Delete -> onEvent(StudentsUiEvent.DeleteStudent(student.id))
                        PendingActionType.ToggleStatus -> onEvent(StudentsUiEvent.ToggleStatus(student.id))
                    }
                    pendingStudentAction = null
                },
                tone = if (pending.action == PendingActionType.Delete) AppConfirmationDialogTone.Danger else AppConfirmationDialogTone.Primary
            )
        }
    }
}

private enum class PendingActionType {
    Delete,
    ToggleStatus
}

private data class PendingStudentAction(
    val student: StudentRowUi,
    val action: PendingActionType
)
